import React from 'react';

import styled from '@emotion/styled';

import {
  COLORS,
  FontWeight,
  SANS_SERIF_FONT_FAMILY,
  SERIF_FONT_FAMILY,
  mq,
} from '@clutter/clean';

import boxIcon from './images/box_icon.svg';
import checkIcon from './images/check.svg';
import clutterCIcon from './images/clutter_c.svg';
import dollarSignIcon from './images/dollar_sign_icon.svg';
import handPackageIcon from './images/hand_package_icon.svg';
import localShippingIcon from './images/local_shipping_icon.svg';
import toolsIcon from './images/tools_icon.svg';
import xIcon from './images/x.svg';

const Container = styled.div`
  background: ${COLORS.tealJungle};
  position: relative;
  z-index: 2;
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  ${mq({ padding: ['48px 0', '80px 80px'] })}
`;

const ComparisonTitle = styled.h2`
  font-family: ${SERIF_FONT_FAMILY};
  font-weight: ${FontWeight.Medium};
  color: ${COLORS.cloud};
  line-height: 44px;
  ${mq({
    fontSize: ['40px', null, '45px'],
    lineHeight: ['44px', null, '48px'],
    textAlign: ['center', null, 'left'],
    marginBottom: ['28px', null, '0px'],
    padding: ['0 24px', null, '0'],
    gridColumn: [null, null, '1'],
  })}
`;

const HeaderRow = styled.div`
  display: grid;
  ${mq({
    display: ['flex', null, 'grid'],
    flexDirection: ['column', null, 'initial'],
    gridTemplateColumns: [null, null, '1fr 320px'],
    gap: [null, null, '0'],
  })}
`;

const ContentBlock = styled.div`
  ${mq({
    display: ['flex', null, 'contents'],
    flexDirection: ['column', null, 'initial'],
    gap: ['12px', null, '0'],
  })}
`;

const HeaderOptionsRow = styled.div`
  display: flex;
  align-items: center;
  ${mq({
    display: ['grid', null, 'flex'],
    gridTemplateColumns: ['1fr 1fr', null, 'initial'],
    gap: ['12px', null, '12px'],
    padding: ['0 18px 0 35px', null, '0'],
    justifyContent: ['flex-start', null, 'initial'],
    gridColumn: [null, null, '2'],
  })}

  /* Hide spacer on desktop - it’s only for mobile grid alignment */
  > div:first-of-type {
    ${mq({
      display: ['block', null, 'none'],
    })}
  }
`;

const Row = styled.div<{ background?: string }>`
  display: grid;
  background: ${(props) => props.background ?? 'transparent'};
  padding: 18px;
  border-radius: 8px;
  ${mq({
    gridTemplateColumns: ['1fr', null, '1fr 320px'],
    alignItems: ['center', null, 'initial'],
    justifyContent: ['center', null, 'initial'],
  })}
`;

const FeatureRow = styled(Row)`
  ${mq({
    gridTemplateColumns: ['1fr 1fr', null, '1fr 320px'],
    padding: ['18px 18px 18px 35px', null, '18px'],
    borderRadius: ['0', null, '8px'],
    gap: ['12px', null, '0'],
    gridColumn: [null, null, '1 / -1'],
  })}
`;

const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  ${mq({
    gap: ['24px', null, '12px'],
    alignItems: ['center', null, 'initial'],
    justifyItems: ['center', null, 'initial'],
    justifySelf: ['initial', null, 'initial'],
  })}
`;

const HeaderOptions = styled(Options)`
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const Header = styled.h3`
  font-family: ${SERIF_FONT_FAMILY};
  font-weight: ${FontWeight.Medium};
  color: ${COLORS.cloud};
  text-align: center;
  margin: 0;
  ${mq({
    fontSize: ['14px', null, '28px'],
    lineHeight: ['1.2', null, '1.2'],
    maxWidth: ['103px', null, 'none'],
    width: ['auto', null, 'auto'],
  })}
`;

const ClutterLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  ${mq({
    height: ['auto', null, '48px'],
    transform: [null, null, 'translateX(-15px)'],
  })}
`;

const ClutterLogoImg = styled.img`
  filter: brightness(0) invert(1);
  ${mq({
    width: ['24px', null, '44px'],
    height: ['26px', null, '48px'],
  })}
`;

const FeatureLabel = styled.div`
  display: inline-flex;
  ${mq({
    flexDirection: ['row', null, 'row'],
    alignItems: ['center', null, 'center'],
    gap: ['0', null, '12px'],
    padding: ['0', null, '12px'],
    margin: ['0', null, '0'],
    width: ['auto', null, 'auto'],
  })}
`;

const FeatureIcon = styled.img`
  filter: brightness(0) invert(1);
  ${mq({
    display: ['none', null, 'block'],
    width: ['24px', null, '30px'],
    height: ['24px', null, '30px'],
  })}
`;

const FeatureText = styled.span`
  font-family: ${SANS_SERIF_FONT_FAMILY};
  font-weight: 500;
  color: ${COLORS.cloud};
  ${mq({
    fontSize: ['14px', null, '24px'],
    lineHeight: ['1.2', null, '1.2'],
  })}
`;

const AvailabilityMark = styled.div<{ variant: 'clutter' | 'yes' | 'no' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  ${mq({ height: ['auto', '48px'] })}

  > div {
    ${mq({
      width: ['32px', '48px'],
      height: ['32px', '48px'],
    })}
    border-radius: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(props) =>
      props.variant === 'clutter' ? `${COLORS.tealBackground}` : 'transparent'};
    border: none;

    img {
      ${(props) =>
        props.variant !== 'clutter' ? 'filter: brightness(0) invert(1);' : ''}
    }
  }
`;

const MarkIcon = styled.img<{ variant: 'check' | 'x' }>`
  ${(props) =>
    props.variant === 'check'
      ? mq({
          width: ['16px', null, '24px'],
          height: ['12px', null, '18px'],
        })
      : mq({
          width: ['19px', null, '28px'],
          height: ['19px', null, '28px'],
        })}
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  margin: 0;
  ${mq({
    marginBottom: ['0', null, '12px'],
    gridColumn: [null, null, '1 / -1'],
  })}
`;

const features = [
  {
    icon: handPackageIcon,
    label: 'Pickup & delivery',
    values: ['clutter', 'no'] as const,
  },
  {
    icon: boxIcon,
    label: 'Digital inventory',
    values: ['clutter', 'no'] as const,
  },
  {
    icon: toolsIcon,
    label: 'Packing help & tools',
    values: ['clutter', 'no'] as const,
  },
  {
    icon: dollarSignIcon,
    label: 'Only pay for the exact space you used',
    values: ['clutter', 'no'] as const,
  },
  {
    icon: handPackageIcon,
    label: 'Heavy lifting',
    values: ['clutter', 'no'] as const,
  },
  {
    icon: localShippingIcon,
    label: 'Truck & driver available',
    values: ['clutter', 'yes'] as const,
  },
];

export const Comparison: React.FC = () => {
  return (
    <Container>
      <Inner>
        <HeaderRow>
          <ComparisonTitle>Compare</ComparisonTitle>
          <ContentBlock>
            <HeaderOptionsRow>
              <div aria-hidden="true" />
              <HeaderOptions>
                <ClutterLogo>
                  <ClutterLogoImg src={clutterCIcon} alt="Clutter" />
                </ClutterLogo>
                <Header>
                  Traditional
                  <br />
                  Storage
                </Header>
              </HeaderOptions>
            </HeaderOptionsRow>
            <Divider />
            {features.map((feature, index) => (
              <FeatureRow
                key={feature.label}
                background={index % 2 === 1 ? '#27afa7' : undefined}
              >
                <FeatureLabel>
                  <FeatureIcon src={feature.icon} alt="" />
                  <FeatureText>{feature.label}</FeatureText>
                </FeatureLabel>

                <Options>
                  <AvailabilityMark variant={feature.values[0]}>
                    <div>
                      <MarkIcon
                        src={checkIcon}
                        variant="check"
                        alt="Included"
                      />
                    </div>
                  </AvailabilityMark>

                  <AvailabilityMark variant={feature.values[1]}>
                    <div>
                      {feature.values[1] === 'yes' ? (
                        <MarkIcon
                          src={checkIcon}
                          variant="check"
                          alt="Included"
                        />
                      ) : (
                        <MarkIcon src={xIcon} variant="x" alt="Not included" />
                      )}
                    </div>
                  </AvailabilityMark>
                </Options>
              </FeatureRow>
            ))}
          </ContentBlock>
        </HeaderRow>
      </Inner>
    </Container>
  );
};
